import type { Localized } from './home.content';
import type { TestimonialId } from './testimonials.content';

/**
 * Audience segmentation for the marketing site. One record per audience
 * drives three surfaces so they never drift apart:
 *   - the "Solutions" menu in the marketing header,
 *   - the audience tabs on the landing page,
 *   - the showcase block on each audience page.
 *
 * `demo` keys map to animated product demos in
 * _components/product-demos/demo-registry.tsx. Logos live in
 * public/landing-pages/flow/customers; images are generated editorial shots
 * in public/landing-pages/flow/images/audiences.
 */

export type DemoKey =
  | 'gym-app'
  | 'court-booking'
  | 'class-booking'
  | 'membership'
  | 'enrollment'
  | 'branded-app'
  | 'whatsapp'
  | 'calendar'
  | 'payments'
  | 'crm';

export type SolutionSlug =
  | 'padel-tennis'
  | 'studios'
  | 'gyms'
  | 'membership-clubs'
  | 'enterprise';

export interface SolutionBullet {
  title: Localized;
  body: Localized;
}

export interface Solution {
  slug: SolutionSlug;
  href: string;
  tabLabel: Localized;
  menuLabel: Localized;
  menuDescription: Localized;
  heading: Localized;
  description: Localized;
  bullets: SolutionBullet[];
  image: string;
  imageAlt: Localized;
  demo: DemoKey;
  logos: { name: string; file: string }[];
  testimonial?: TestimonialId;
  cta: Localized;
}

const IMAGE_BASE = '/landing-pages/flow/images/audiences';

export const SOLUTIONS_SECTION = {
  label: { en: 'Who it is for', ar: 'لمن صُممت' },
  heading: {
    en: 'Built for the way your business runs',
    ar: 'مصممة حول طريقة عمل منشأتك',
  },
  description: {
    en: 'Flow is one platform, set up differently for every kind of venue. Pick yours to see what that looks like.',
    ar: 'Flow منصة واحدة تُهيّأ بشكل مختلف لكل نوع من المنشآت. اختر نوع منشأتك لترى كيف تبدو.',
  },
  clientsLabel: { en: 'A few of the venues on Flow', ar: 'بعض المنشآت التي تعمل على Flow' },
} as const;

export const SOLUTIONS: Solution[] = [
  {
    slug: 'padel-tennis',
    href: '/padel-software',
    tabLabel: { en: 'Padel & tennis', ar: 'البادل والتنس' },
    menuLabel: { en: 'Padel & tennis clubs', ar: 'أندية البادل والتنس' },
    menuDescription: {
      en: 'Courts, peak pricing, split payments, coaching',
      ar: 'الملاعب وتسعير الذروة وتقسيم الدفع والتدريب',
    },
    heading: {
      en: 'Keep every court full, from the first slot to the last.',
      ar: 'ملاعب ممتلئة من أول موعد حتى آخر موعد.',
    },
    description: {
      en: 'Online court booking, peak pricing, split payments and coaching programs in one system, booked from an app that carries your club name.',
      ar: 'حجز الملاعب إلكترونيًا وتسعير الذروة وتقسيم الدفع وبرامج التدريب في نظام واحد، من تطبيق يحمل اسم ناديك.',
    },
    bullets: [
      {
        title: { en: 'Peak and off-peak pricing', ar: 'تسعير الذروة وخارجها' },
        body: {
          en: 'Rates per court and time window, applied to every booking automatically.',
          ar: 'أسعار لكل ملعب ولكل فترة زمنية، تُطبق على كل حجز تلقائيًا.',
        },
      },
      {
        title: { en: 'Split payments', ar: 'تقسيم الدفع' },
        body: {
          en: 'One player books, the other three pay their own share from a link.',
          ar: 'يحجز لاعب واحد، ويدفع الثلاثة الآخرون حصصهم عبر رابط.',
        },
      },
      {
        title: { en: 'Coaching and academies', ar: 'التدريب والأكاديميات' },
        body: {
          en: 'Private lessons, clinics and term programs beside open play.',
          ar: 'الدروس الخاصة والحصص الجماعية والبرامج الفصلية إلى جانب اللعب المفتوح.',
        },
      },
      {
        title: { en: 'Bookings over WhatsApp', ar: 'الحجز عبر واتساب' },
        body: {
          en: 'The AI concierge answers availability questions and books inside the chat.',
          ar: 'يجيب المساعد الذكي عن أسئلة التوفر ويُتم الحجز داخل المحادثة.',
        },
      },
    ],
    image: `${IMAGE_BASE}/padel.webp`,
    imageAlt: {
      en: 'Four players in a padel match on a glass-walled court at sunset',
      ar: 'أربعة لاعبين في مباراة بادل على ملعب بجدران زجاجية وقت الغروب',
    },
    demo: 'court-booking',
    logos: [
      { name: 'Padel District', file: 'padel-district-jeddah.png' },
      { name: 'Dunes Racket Club', file: 'dunes-racket-club.png' },
      { name: 'B Padel', file: 'b-padel.jpeg' },
      { name: 'The Racquet Space', file: 'the-racquet-space.png' },
      { name: 'Deem Padel', file: 'deem-padel.png' },
      { name: 'The Lab Court', file: 'the-lab-court.png' },
    ],
    cta: {
      en: 'Explore Flow for padel & tennis',
      ar: 'اكتشف Flow لأندية البادل والتنس',
    },
  },
  {
    slug: 'studios',
    href: '/pilates-studio-software',
    tabLabel: { en: 'Yoga & pilates', ar: 'اليوغا والبيلاتس' },
    menuLabel: { en: 'Yoga & pilates studios', ar: 'استوديوهات اليوغا والبيلاتس' },
    menuDescription: {
      en: 'Class packs, credits, waitlists, branded app',
      ar: 'باقات الحصص والأرصدة وقوائم الانتظار وتطبيقك الخاص',
    },
    heading: {
      en: 'Full classes, a waitlist that runs itself, members who come back.',
      ar: 'حصص ممتلئة، وقائمة انتظار تعمل وحدها، وأعضاء يعودون.',
    },
    description: {
      en: 'Class schedules, credit packs, waitlists and memberships, booked in seconds from a studio app that looks like your studio.',
      ar: 'جداول الحصص وباقات الأرصدة وقوائم الانتظار والعضويات، تُحجز خلال ثوانٍ من تطبيق يشبه استوديوك.',
    },
    bullets: [
      {
        title: { en: 'Packs and credits', ar: 'الباقات والأرصدة' },
        body: {
          en: 'Class packs with expiry windows; balances update on every booking.',
          ar: 'باقات حصص بصلاحية محددة، وتُحدَّث الأرصدة مع كل حجز.',
        },
      },
      {
        title: { en: 'Automatic waitlists', ar: 'قوائم انتظار تلقائية' },
        body: {
          en: 'When a spot opens, the next client is confirmed and notified.',
          ar: 'عندما يتوفر مكان، يُؤكَّد حجز العميل التالي ويصله إشعار.',
        },
      },
      {
        title: { en: 'Instructor calendars', ar: 'تقاويم المدربين' },
        body: {
          en: 'Each instructor sees their classes; attendance is recorded per session.',
          ar: 'يرى كل مدرب حصصه، ويُسجَّل الحضور لكل حصة.',
        },
      },
      {
        title: { en: 'Your own studio app', ar: 'تطبيق باسم استوديوك' },
        body: {
          en: 'Members book from an app with your name and colours on it.',
          ar: 'يحجز الأعضاء من تطبيق يحمل اسمك وألوانك.',
        },
      },
    ],
    image: `${IMAGE_BASE}/studio.webp`,
    imageAlt: {
      en: 'A reformer pilates class in a bright studio with an instructor adjusting a client',
      ar: 'حصة بيلاتس ريفورمر في استوديو مشرق ومدربة تعدّل وضعية إحدى المتدربات',
    },
    demo: 'class-booking',
    logos: [
      { name: 'Frame Studio', file: 'frame-studio.png' },
      { name: 'The Pilates Room', file: 'the-pilates-room.png' },
      { name: 'Refine Studio', file: 'refine-studio.png' },
      { name: 'Flex&Flow', file: 'flexflow.png' },
    ],
    testimonial: 'frame-studio',
    cta: {
      en: 'Explore Flow for studios',
      ar: 'اكتشف Flow للاستوديوهات',
    },
  },
  {
    slug: 'gyms',
    href: '/gym-software',
    tabLabel: { en: 'Gyms & fitness', ar: 'الصالات الرياضية' },
    menuLabel: { en: 'Gyms & fitness clubs', ar: 'الصالات والأندية الرياضية' },
    menuDescription: {
      en: 'Memberships, freezes, check-in, classes, PT',
      ar: 'العضويات والتجميد وتسجيل الدخول والحصص والتدريب الشخصي',
    },
    heading: {
      en: 'Memberships, classes and the front desk, in one system.',
      ar: 'العضويات والحصص والاستقبال، في نظام واحد.',
    },
    description: {
      en: 'From the gate scan to the protein shake: memberships, freezes, classes, personal training and front-desk sales for gyms and fitness clubs, with an app that carries your name.',
      ar: 'من مسح البطاقة عند البوابة حتى مشروب البروتين: العضويات والتجميد والحصص والتدريب الشخصي ومبيعات الاستقبال للصالات والأندية الرياضية، من تطبيق يحمل اسمك.',
    },
    bullets: [
      {
        title: { en: 'Memberships and freezes', ar: 'العضويات والتجميد' },
        body: {
          en: 'Monthly and annual plans with member pricing, and freezes within the rules you set.',
          ar: 'خطط شهرية وسنوية بأسعار الأعضاء، وتجميد العضوية ضمن القواعد التي تحددها.',
        },
      },
      {
        title: { en: 'Gate check-in', ar: 'تسجيل الدخول عند البوابة' },
        body: {
          en: 'Members scan their digital card at the gate, and staff see every visit.',
          ar: 'يمسح الأعضاء بطاقتهم الرقمية عند البوابة، ويرى فريقك كل زيارة.',
        },
      },
      {
        title: { en: 'Classes, waitlists and PT', ar: 'الحصص وقوائم الانتظار والتدريب الشخصي' },
        body: {
          en: 'Class capacity with automatic waitlists, and PT packs that draw down per session.',
          ar: 'سعة لكل حصة مع قوائم انتظار تلقائية، وباقات تدريب شخصي تُخصم مع كل جلسة.',
        },
      },
      {
        title: { en: 'Front desk and renewals', ar: 'الاستقبال والتجديد' },
        body: {
          en: 'Sell shakes and gear at the desk, and send renewal reminders on WhatsApp with a payment link.',
          ar: 'بِع المشروبات والمستلزمات من الاستقبال، وأرسل تذكيرات التجديد عبر واتساب برابط دفع.',
        },
      },
    ],
    image: `${IMAGE_BASE}/gym.webp`,
    imageAlt: {
      en: 'A personal trainer coaching a client through a kettlebell squat in a modern gym',
      ar: 'مدرب شخصي يوجّه متدربة أثناء تمرين الكيتلبل في صالة رياضية حديثة',
    },
    demo: 'gym-app',
    logos: [{ name: 'Flex&Flow', file: 'flexflow.png' }],
    testimonial: 'flex-flow',
    cta: {
      en: 'Explore Flow for gyms',
      ar: 'اكتشف Flow للصالات الرياضية',
    },
  },
  {
    slug: 'membership-clubs',
    href: '/membership-club-software',
    tabLabel: { en: 'Membership clubs', ar: 'أندية العضويات' },
    menuLabel: { en: 'Membership clubs', ar: 'أندية العضويات' },
    menuDescription: {
      en: 'Tiers, family accounts, guest passes, check-in',
      ar: 'الفئات والحسابات العائلية وتصاريح الضيوف وتسجيل الدخول',
    },
    heading: {
      en: 'A members club experience, from the front gate to renewal day.',
      ar: 'تجربة نادي أعضاء متكاملة، من البوابة حتى يوم التجديد.',
    },
    description: {
      en: 'Digital membership cards, family accounts, guest passes and check-in, with events and renewals handled in the same app.',
      ar: 'بطاقات عضوية رقمية وحسابات عائلية وتصاريح ضيوف وتسجيل دخول، مع الفعاليات والتجديد في التطبيق نفسه.',
    },
    bullets: [
      {
        title: { en: 'Tiers and family memberships', ar: 'فئات وعضويات عائلية' },
        body: {
          en: 'Member pricing and booking windows per tier, with children on one account.',
          ar: 'أسعار ونوافذ حجز خاصة بكل فئة، مع إضافة الأبناء إلى حساب واحد.',
        },
      },
      {
        title: { en: 'Guest passes', ar: 'تصاريح الضيوف' },
        body: {
          en: 'Members invite guests themselves, and the front desk sees who is coming.',
          ar: 'يدعو الأعضاء ضيوفهم بأنفسهم، ويعرف الاستقبال مسبقًا من سيحضر.',
        },
      },
      {
        title: { en: 'Check-in at the door', ar: 'تسجيل الدخول عند البوابة' },
        body: {
          en: 'A digital card with a code your front desk scans in a second.',
          ar: 'بطاقة رقمية برمز يمسحه موظف الاستقبال خلال ثانية.',
        },
      },
      {
        title: { en: 'Events, wallet and POS', ar: 'الفعاليات والمحفظة ونقطة البيع' },
        body: {
          en: 'Tickets, top-ups and counter sales land in the same ledger.',
          ar: 'التذاكر وشحن الرصيد ومبيعات الكاونتر في سجل واحد.',
        },
      },
    ],
    image: `${IMAGE_BASE}/membership.webp`,
    imageAlt: {
      en: 'Members relaxing on the pool terrace of a private sports club at dusk',
      ar: 'أعضاء يسترخون على شرفة المسبح في نادٍ رياضي خاص عند الغسق',
    },
    demo: 'membership',
    logos: [{ name: 'OIA', file: 'oia.png' }],
    cta: {
      en: 'Explore Flow for membership clubs',
      ar: 'اكتشف Flow لأندية العضويات',
    },
  },
  {
    slug: 'enterprise',
    href: '/enterprise',
    tabLabel: { en: 'Enterprise & schools', ar: 'المؤسسات والمدارس' },
    menuLabel: { en: 'Enterprise & schools', ar: 'المؤسسات والمدارس' },
    menuDescription: {
      en: 'Programs, contracts, many locations',
      ar: 'البرامج والعقود والمواقع المتعددة',
    },
    heading: {
      en: 'Programs, contracts and many locations, run from one place.',
      ar: 'البرامج والعقود والمواقع المتعددة، من مكان واحد.',
    },
    description: {
      en: 'For schools, academies and multi-site operators: term programs, family accounts, recurring contracts with quotes and invoices, and staff roles scoped by location.',
      ar: 'للمدارس والأكاديميات والمشغلين متعددي المواقع: برامج فصلية وحسابات عائلية وعقود متكررة بعروض أسعار وفواتير، وصلاحيات موظفين حسب الموقع.',
    },
    bullets: [
      {
        title: { en: 'Term programs', ar: 'البرامج الفصلية' },
        body: {
          en: 'Capacity, coach rosters and attendance for every group and session.',
          ar: 'السعة وقوائم المدربين والحضور لكل مجموعة وحصة.',
        },
      },
      {
        title: { en: 'Family accounts', ar: 'الحسابات العائلية' },
        body: {
          en: 'Parents enroll and pay for their children from one account.',
          ar: 'يسجّل أولياء الأمور أبناءهم ويدفعون عنهم من حساب واحد.',
        },
      },
      {
        title: { en: 'Contracts for organizations', ar: 'عقود الجهات' },
        body: {
          en: 'Recurring sessions for schools and companies, quoted and invoiced to the organization.',
          ar: 'حصص متكررة للمدارس والشركات، بعرض سعر وفاتورة باسم الجهة.',
        },
      },
      {
        title: { en: 'Built for many sites', ar: 'مصممة للمواقع المتعددة' },
        body: {
          en: 'Location-scoped roles, per-site WhatsApp numbers and accounting integrations.',
          ar: 'صلاحيات حسب الموقع، وأرقام واتساب لكل فرع، وتكامل مع الأنظمة المحاسبية.',
        },
      },
    ],
    image: `${IMAGE_BASE}/enterprise.webp`,
    imageAlt: {
      en: 'Aerial view at dusk of a multi-sport campus with padel courts, tennis courts and a football pitch',
      ar: 'منظر جوي عند الغسق لمجمع رياضي يضم ملاعب بادل وتنس وملعب كرة قدم',
    },
    demo: 'enrollment',
    logos: [
      { name: 'Ryze Tennis', file: 'ryze-tennis-academy.png' },
      { name: 'Net Academy', file: 'net-tennis-academy.png' },
      { name: 'Gaby Reca Academy', file: 'gaby-reca-academy.png' },
      { name: 'Red Sea Sports Club', file: 'red-sea-sports-club.jpg' },
    ],
    testimonial: 'gaby-reca',
    cta: {
      en: 'Explore Flow for enterprise',
      ar: 'اكتشف Flow للمؤسسات',
    },
  },
];

/** Secondary audiences shown after the primary ones in the Solutions menu. */
export const SOLUTIONS_SECONDARY: {
  href: string;
  label: Localized;
  description: Localized;
}[] = [
  {
    href: '/academy-software',
    label: { en: 'Sports academies', ar: 'الأكاديميات الرياضية' },
    description: {
      en: 'Term programs, coaches, parent payments',
      ar: 'البرامج الفصلية والمدربون ومدفوعات أولياء الأمور',
    },
  },
];

export const SOLUTIONS_MENU = {
  trigger: { en: 'Solutions', ar: 'الحلول' },
  alsoFor: { en: 'Also for', ar: 'وأيضًا' },
  viewCustomers: { en: 'Meet some of our clients', ar: 'تعرّف على بعض عملائنا' },
} as const;

export function findSolution(slug: SolutionSlug): Solution {
  const solution = SOLUTIONS.find((entry) => entry.slug === slug);

  if (!solution) {
    throw new Error(`Unknown solution: ${slug}`);
  }

  return solution;
}
